import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/services';
import {
  micrositeService,
  type MicrositeDevice,
  type MicrositePageType,
  type MicrositeSection,
  type PublicSiteUpdate,
  type MicrositeVisibilityItemType,
  type ThemeTokens,
} from '@/services/microsite';

const LAYOUT_KEY = 'micrositeLayout';
const SECTION_TYPES_KEY = 'micrositeSectionTypes';
const PUBLIC_SLUG_KEY = 'micrositePublicSlug';
const PUBLIC_SITE_KEY = 'micrositePublicSite';
const CATALOG_KEY = 'micrositeCatalog';
const THEME_KEY = 'micrositeTheme';
const SECTION_TEMPLATES_KEY = 'micrositeSectionTemplates';
const PREVIEW_KEY = 'micrositePreview';

/**
 * The PUT validator returns a bare ``{ sections: ["err", ...] }`` body (not the
 * standardized error envelope), so pull those messages out before falling back
 * to the generic error parser. `tokens` is the theme endpoint's equivalent.
 */
function micrositeError(error: unknown): string {
  const anyErr = error as {
    response?: { data?: { sections?: unknown; tokens?: unknown; theme?: unknown } };
  };
  const data = anyErr?.response?.data;
  for (const key of ['sections', 'tokens', 'theme'] as const) {
    const list = data?.[key];
    if (Array.isArray(list) && list.length > 0) return String(list[0]);
  }
  return getErrorMessage(error);
}

export function useSectionTypes() {
  return useQuery({
    queryKey: [SECTION_TYPES_KEY],
    queryFn: () => micrositeService.getSectionTypes(),
    staleTime: 1000 * 60 * 30, // registry rarely changes
  });
}

export function useMicrositeLayout(pageType: MicrositePageType) {
  return useQuery({
    queryKey: [LAYOUT_KEY, pageType],
    queryFn: () => micrositeService.getLayout(pageType),
  });
}

export function useUpdateMicrositeLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pageType,
      sections,
      isPublished,
    }: {
      pageType: MicrositePageType;
      sections: MicrositeSection[];
      isPublished?: boolean;
    }) => micrositeService.updateLayout(pageType, sections, isPublished ?? true),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [LAYOUT_KEY, vars.pageType] });
      toast.success('Page saved');
    },
    onError: (e) => toast.error(micrositeError(e)),
  });
}

export function useCustomizeMicrositeLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageType: MicrositePageType) => micrositeService.customize(pageType),
    onSuccess: (_data, pageType) => {
      qc.invalidateQueries({ queryKey: [LAYOUT_KEY, pageType] });
      toast.success('Customized from base template');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useResetMicrositeToBase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageType: MicrositePageType) => micrositeService.resetToBase(pageType),
    onSuccess: (_data, pageType) => {
      qc.invalidateQueries({ queryKey: [LAYOUT_KEY, pageType] });
      toast.success('Reset to base template');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useSubmitMicrositeDomain() {
  return useMutation({
    mutationFn: (domain: string) => micrositeService.submitDomain(domain),
    onSuccess: () => toast.success('Domain submitted for verification'),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useApproveMicrositeDomain() {
  return useMutation({
    mutationFn: (domain: string) => micrositeService.approveDomain(domain),
    onSuccess: (data) => toast.success(`Approved ${data.domain} for ${data.broker}`),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useMicrositePublicSlug(brokerId?: string) {
  return useQuery({
    queryKey: [PUBLIC_SLUG_KEY, brokerId],
    queryFn: () => micrositeService.getPublicSlug(brokerId!),
    enabled: !!brokerId,
    staleTime: 1000 * 60 * 5,
  });
}

/** The broker's own public-site status (enabled flag, slug, live URL). */
export function usePublicSiteStatus() {
  return useQuery({
    queryKey: [PUBLIC_SITE_KEY],
    queryFn: () => micrositeService.getPublicSiteStatus(),
    staleTime: 1000 * 30,
  });
}

/** Self-service toggle: enable/disable the public site, set slug/description. */
export function useUpdatePublicSiteStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PublicSiteUpdate) => micrositeService.updatePublicSiteStatus(payload),
    onSuccess: (data) => {
      qc.setQueryData([PUBLIC_SITE_KEY], data);
      toast.success(
        data.is_public_profile_enabled ? 'Your public site is now live.' : 'Public site turned off.',
      );
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── Products & Pages visibility ────────────────────────────────────────────────

/** The broker's catalog with per-item public-page visibility. */
export function useMicrositeCatalog() {
  return useQuery({
    queryKey: [CATALOG_KEY],
    queryFn: () => micrositeService.getCatalog(),
    staleTime: 1000 * 30,
  });
}

/** Show/hide a single insurer or product page on the public site. */
export function useSetItemVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemType,
      itemId,
      visible,
    }: {
      itemType: MicrositeVisibilityItemType;
      itemId: string;
      visible: boolean;
    }) => micrositeService.setItemVisibility(itemType, itemId, visible),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [CATALOG_KEY] });
      const noun = vars.itemType === 'insurer' ? 'Company' : 'Product';
      toast.success(
        vars.visible
          ? `${noun} is now public`
          : `${noun} hidden from your public site`,
      );
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

/** Master switch: clear the whole hide-list so every page is public again. */
export function useMakeAllPublic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => micrositeService.makeAllPublic(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CATALOG_KEY] });
      toast.success('Everything is now public');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── Theme ──────────────────────────────────────────────────────────────────────

/** The effective theme for the caller's scope, plus the server's field schema. */
export function useMicrositeTheme() {
  return useQuery({
    queryKey: [THEME_KEY],
    queryFn: () => micrositeService.getTheme(),
    staleTime: 1000 * 30,
  });
}

export function useSaveMicrositeTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokens: ThemeTokens) => micrositeService.saveTheme(tokens),
    onSuccess: (data) => {
      qc.setQueryData([THEME_KEY], data);
      // The theme changes what every page renders, so any open preview is stale.
      qc.invalidateQueries({ queryKey: [PREVIEW_KEY] });
      toast.success('Theme saved');
    },
    onError: (e) => toast.error(micrositeError(e)),
  });
}

export function useResetMicrositeTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => micrositeService.resetTheme(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [THEME_KEY] });
      qc.invalidateQueries({ queryKey: [PREVIEW_KEY] });
      toast.success('Theme reset to the platform default');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── Reusable section templates ─────────────────────────────────────────────────

export function useSectionTemplates() {
  return useQuery({
    queryKey: [SECTION_TEMPLATES_KEY],
    queryFn: () => micrositeService.listSectionTemplates(),
    staleTime: 1000 * 60,
  });
}

export function useSaveSectionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      section_type: string;
      config: Record<string, unknown>;
    }) => micrositeService.createSectionTemplate(payload),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [SECTION_TEMPLATES_KEY] });
      toast.success(`Saved "${data.name}" — reuse it from Add a section`);
    },
    onError: (e) => toast.error(micrositeError(e)),
  });
}

export function useDeleteSectionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => micrositeService.deleteSectionTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SECTION_TEMPLATES_KEY] });
      toast.success('Template deleted');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ─── Live preview ───────────────────────────────────────────────────────────────

/**
 * Server-rendered preview of the sections currently on screen.
 *
 * Keyed by the *content* of the draft (sections + theme + device + page), so a
 * keystroke that changes nothing meaningful reuses the cached render and an edit
 * refetches exactly once. `enabled` lets the caller keep the preview pane closed
 * without paying for a render.
 */
export function useMicrositePreview({
  pageType,
  sections,
  device,
  theme,
  enabled = true,
}: {
  pageType: MicrositePageType;
  sections: MicrositeSection[];
  device: MicrositeDevice;
  theme?: ThemeTokens | null;
  enabled?: boolean;
}) {
  const signature = JSON.stringify({ sections, theme: theme ?? null });
  return useQuery({
    queryKey: [PREVIEW_KEY, pageType, device, signature],
    queryFn: () => micrositeService.renderPreview({ pageType, sections, device, theme }),
    enabled,
    // A draft render is only ever valid for the exact draft in the key.
    staleTime: 1000 * 60 * 5,
    retry: false,
    // Keep the last good frame on screen while the next one renders, instead of
    // flashing an empty pane on every keystroke.
    placeholderData: (previous) => previous,
  });
}
